import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeNorwegianPhone } from '../../utils/phone';
import Field from '../carrier/Field';
import { ui } from '../carrier/carrierStyles';

const MESSAGES = {
  'auth/invalid-verification-code': 'Feil kode. Prøv igjen.',
  'auth/code-expired': 'Koden har utløpt. Be om en ny.',
  'auth/too-many-requests': 'For mange forsøk. Vent litt og prøv igjen.',
  'auth/invalid-phone-number': 'Ugyldig mobilnummer.',
};
const messageFor = (err) => MESSAGES[err?.code] ?? 'Kunne ikke logge inn. Prøv igjen.';

// Innlogging for sjåfører og ansatte som er lagt til av et transportfirma: mobilnummer og SMS-kode.
export default function PhoneLogin() {
  const navigate = useNavigate();
  const { currentUser, effectiveRole } = useAuth();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const verifierRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (currentUser && effectiveRole) navigate('/');
  }, [currentUser, effectiveRole, navigate]);

  useEffect(() => () => {
    if (verifierRef.current) verifierRef.current.clear();
    verifierRef.current = null;
  }, []);

  function getVerifier() {
    if (!verifierRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, containerRef.current, { size: 'invisible' });
    }
    return verifierRef.current;
  }

  function resetVerifier() {
    if (verifierRef.current) verifierRef.current.clear();
    verifierRef.current = null;
  }

  async function sendCode(e) {
    e.preventDefault();
    setError('');
    const e164 = normalizeNorwegianPhone(phone);
    if (!e164) { setError('Skriv inn et norsk mobilnummer'); return; }
    setBusy(true);
    try {
      setConfirmation(await signInWithPhoneNumber(auth, e164, getVerifier()));
    } catch (err) {
      resetVerifier();
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const cred = await confirmation.confirm(code.trim());
      // Firebase oppretter en Auth-bruker for ethvert nummer. Uten brukerdokument (eller uten roller) er nummeret ikke lagt til av et firma.
      let registered = false;
      let lookupFailed = false;
      try {
        const snap = await getDoc(doc(db, 'users', cred.user.uid));
        registered = snap.exists() && Array.isArray(snap.data()?.roles) && snap.data().roles.length > 0;
      } catch {
        lookupFailed = true;
      }
      if (!registered) {
        await signOut(auth);
        setConfirmation(null);
        setCode('');
        resetVerifier();
        setError(lookupFailed
          ? 'Kunne ikke logge inn. Prøv igjen.'
          : 'Nummeret er ikke registrert hos et transportfirma. Be din dispatcher om å legge deg til.');
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ ...ui.main, maxWidth: 420 }}>
      <h1 style={ui.h1}>Logg inn med mobil</h1>
      <div style={ui.card}>
        {!confirmation ? (
          <form onSubmit={sendCode} noValidate>
            <p style={ui.hint}>Skriv inn mobilnummeret ditt, så sender vi en kode på SMS.</p>
            <Field id="pl-phone" label="Mobilnummer">
              <input id="pl-phone" style={ui.input} value={phone} onChange={e => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
            </Field>
            {error && <p role="alert" style={ui.error}>{error}</p>}
            <button type="submit" className="tippi-btn" style={{ ...ui.btn, marginTop: 14, width: '100%' }} disabled={busy}>
              {busy ? 'Sender…' : 'Send kode'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} noValidate>
            <p style={ui.hint}>Vi har sendt en kode til {phone}.</p>
            <Field id="pl-code" label="Kode fra SMS">
              <input id="pl-code" style={ui.input} value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" />
            </Field>
            {error && <p role="alert" style={ui.error}>{error}</p>}
            <button type="submit" className="tippi-btn" style={{ ...ui.btn, marginTop: 14, width: '100%' }} disabled={busy}>
              {busy ? 'Logger inn…' : 'Logg inn'}
            </button>
            <button type="button" style={{ ...ui.btnSecondary, marginTop: 10, width: '100%' }} onClick={() => { setConfirmation(null); setCode(''); setError(''); resetVerifier(); }}>
              Bruk et annet nummer
            </button>
          </form>
        )}
        <div ref={containerRef} />
      </div>
      <p style={ui.hint}><Link to="/login" style={ui.link}>Logg inn med e-post i stedet</Link></p>
    </main>
  );
}
